/**
 * 要素の中にフォーカスがあるかを返すフック
 *
 * React の onFocus / onBlur だけで判定すると、フォーカス中の要素が消えたり無効化されたりしたときに
 * ブラウザが blur を出さずにフォーカスを外すため、「フォーカス中」のまま戻らなくなる。
 * ここでは通知に頼らず、フォーカス移動とクリックのたびに実際のフォーカス位置で判定し直す。
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null)
 * const isFocusWithin = useFocusWithin(ref)
 * <div ref={ref}>...</div>
 */

import { type RefObject, useEffect, useState } from 'react'

export const useFocusWithin = <T extends HTMLElement>(ref: RefObject<T | null>): boolean => {
  const [isFocusWithin, setIsFocusWithin] = useState(false)

  useEffect(() => {
    const isInside = (node: EventTarget | null): boolean => node instanceof Node && !!ref.current?.contains(node)

    const handleFocusIn = (e: FocusEvent) => setIsFocusWithin(isInside(e.target))
    const handleFocusOut = (e: FocusEvent) => setIsFocusWithin(isInside(e.relatedTarget))
    // フォーカスが黙って外れていた場合、外側のクリックではフォーカスイベントが起きないのでここで判定し直す。
    // 内側のクリック (フォーカスを奪わないチップ等) では、操作中の UI を閉じないよう判定しない
    const handlePointerDown = (e: PointerEvent) => {
      if (isInside(e.target)) return
      setIsFocusWithin(isInside(document.activeElement))
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [ref])

  return isFocusWithin
}
